import CredentialsProvider from 'next-auth/providers/credentials';

type SinglePlayerUser = {
  id: string;
  email: string;
  userName: string;
  fullName: string;
  picture: string | null;
};

type SinglePlayerConfig = {
  enabled: boolean;
  user: SinglePlayerUser;
};

/**
 * Check if single-player mode is enabled.
 * Used in middleware where the config object may not be available.
 */
export function isSinglePlayerMode(): boolean {
  return process.env.AUTH_MODE === 'single-player';
}

/**
 * Configuration for single-player mode.
 * When enabled, authentication is bypassed and a mock user is created.
 */
export const SINGLE_PLAYER_CONFIG: SinglePlayerConfig = {
  enabled: isSinglePlayerMode(),
  user: {
    id: 'single-player-user',
    email: process.env.SINGLE_PLAYER_EMAIL || 'admin@localhost',
    userName: process.env.SINGLE_PLAYER_USERNAME || 'admin',
    fullName: process.env.SINGLE_PLAYER_NAME || 'Local Admin',
    picture: null
  }
};

/**
 * Generate a JWT-like token structure for single-player mode.
 * This token doesn't need to be cryptographically valid since the
 * API gateway bypasses authentication in single-player mode.
 */
export function generateSinglePlayerToken(): string {
  const header = { alg: 'none', typ: 'JWT' };
  const payload = {
    sub: SINGLE_PLAYER_CONFIG.user.id,
    email: SINGLE_PLAYER_CONFIG.user.email,
    preferred_username: SINGLE_PLAYER_CONFIG.user.userName,
    name: SINGLE_PLAYER_CONFIG.user.fullName,
    realm_access: { roles: ['admin'] }, // Always admin in single-player mode
    iat: Math.floor(Date.now() / 1000),
    exp: Math.floor(Date.now() / 1000) + 86400 // 24 hours
  };

  const encode = (obj: object) =>
    Buffer.from(JSON.stringify(obj)).toString('base64url');

  return `${encode(header)}.${encode(payload)}.`;
}

/**
 * Credentials provider that auto-accepts any credentials for single-player mode.
 * This provider is used instead of Keycloak when AUTH_MODE=single-player.
 */
export function SinglePlayerCredentialsProvider() {
  return CredentialsProvider({
    id: 'single-player',
    name: 'Single Player',
    credentials: {}, // No credentials required
    async authorize() {
      // Always return the configured user
      return {
        id: SINGLE_PLAYER_CONFIG.user.id,
        email: SINGLE_PLAYER_CONFIG.user.email,
        name: SINGLE_PLAYER_CONFIG.user.fullName,
        userName: SINGLE_PLAYER_CONFIG.user.userName,
        fullName: SINGLE_PLAYER_CONFIG.user.fullName,
        picture: SINGLE_PLAYER_CONFIG.user.picture
      };
    }
  });
}
